/**
 * Minimal Markdown to HTML conversion for texts from DigitaltMuseum's article editor.
 * Supports paragraphs, line breaks, headings, bold, italic, ordered/unordered lists and block quotes.
 * All other HTML is escaped. Links get `data-href` instead of `href`, so they can not be followed:
 * the view decides what a tap does (e.g. show a QR code).
 */

const escapeHtml = (text: string): string => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Make a link target absolute. Relative links point to DigitaltMuseum. Only web links are kept */
const linkTarget = (url: string): string | undefined => {
  try {
    // Repair common typos such as "https//..." and "https://https//..."
    const repaired = url.trim().replace(/^(https?:\/\/)+(?=https?:?\/\/)/i, "").replace(/^(https?):?\/\/+/i, "$1://");
    const target = new URL(repaired, "https://digitaltmuseum.org");
    return ["http:", "https:"].includes(target.protocol) ? target.href : undefined;
  } catch (e) {
    return undefined;
  }
}

/** Inline formatting within one line. Links are set aside first, so their URLs are not formatted */
const inline = (text: string): string => {
  const links: string[] = [];
  const formatted = escapeHtml(text)
    .replace(/\[([^\]]*)\]\(([^)\s]*)[^)]*\)/g, (_, label, url) => {
      const target = linkTarget(url.replace(/&amp;/g, "&"));
      links.push(target ? `<a data-href="${escapeHtml(target)}">${label || escapeHtml(target)}</a>` : label);
      return `\u0000${links.length - 1}\u0000`;
    })
    .replace(/\*\*(.+?)\*\*|__(.+?)__/g, (_, a, b) => `<strong>${a ?? b}</strong>`)
    .replace(/(^|[^*\w])\*(?!\s)(.+?)\*(?![*\w])/g, "$1<em>$2</em>")
    .replace(/(^|[^_\w])_(?!\s)(.+?)_(?![_\w])/g, "$1<em>$2</em>");
  return formatted.replace(/\u0000(\d+)\u0000/g, (_, i) => links[Number(i)]);
}

const LIST_ITEM = /^\s*(?:[-*+]|(\d+)\.)\s+(.*)$/;

/**
 * Convert Markdown to HTML
 * @param {string} markdown Markdown text
 * @param {number} headingOffset Added to heading levels, e.g. 1 makes "##" an h3
 * @returns {string} HTML
 */
export const markdownToHtml = (markdown?: string, headingOffset: number = 1): string => {
  // The editor sometimes stores <br> and <a> tags in the Markdown. Links are converted to Markdown links
  const lines = String(markdown || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<a\b[^>]*?href=["']([^"']*)["'][^>]*>(.*?)<\/a>/gi, "[$2]($1)")
    .replace(/<\/?a\b[^>]*>/gi, "")
    .split(/\r?\n/);

  const html: string[] = [];
  let paragraph: string[] = [];
  let list: { tag: "ul" | "ol", items: string[] } | null = null;
  let quote: string[] = [];

  const flush = () => {
    paragraph.length && html.push(`<p>${paragraph.map(inline).join("<br>")}</p>`);
    list && html.push(`<${list.tag}>${list.items.map(item => `<li>${inline(item)}</li>`).join("")}</${list.tag}>`);
    quote.length && html.push(`<blockquote>${markdownToHtml(quote.join("\n"), headingOffset)}</blockquote>`);
    paragraph = [];
    list = null;
    quote = [];
  };

  lines.forEach(line => {
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    const listItem = line.match(LIST_ITEM);
    const quoteLine = line.match(/^\s*>\s?(.*)$/);

    if (!line.trim()) {
      flush();
    } else if (heading) {
      flush();
      const level = Math.min(heading[1].length + headingOffset, 6);
      html.push(`<h${level}>${inline(heading[2])}</h${level}>`);
    } else if (quoteLine) {
      quote.length || flush();
      quote.push(quoteLine[1]);
    } else if (listItem) {
      const tag = listItem[1] ? "ol" : "ul";
      if (!list || list.tag !== tag) {
        flush();
        list = { tag, items: [] };
      }
      list.items.push(listItem[2]);
    } else if (list && /^\s+\S/.test(line)) {
      // Continuation of a list item
      list.items[list.items.length - 1] += ` ${line.trim()}`;
    } else {
      (list || quote.length) && flush();
      paragraph.push(line);
    }
  });
  flush();

  return html.join("");
}
